import React from 'react'
// libs
import styled from 'styled-components'
import { useRouter } from 'hooks/useRouter'
import { useSearchParams } from 'react-router'
import { useMutation, useQuery } from '@apollo/client/react'
// hooks
import { useCurrentUser } from 'hooks/useCurrentUser'
// components
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import ConfirmDeleteModal from 'components/Modals/ConfirmDeleteModal'
import { TablePagination } from '@mui/material'
// constants
import { RISKS_PER_PAGE } from 'constants/table'
// containers
import RisksContainer from 'components/PageComponents/Risks/Register/components/RiskView/components/RisksContainer'
// graphql
import { RISKS_QUERY } from 'graphql/risk/queries'
import { RISK_DELETE_MUTATION } from 'graphql/risk/mutations'
// types
import { NetworkStatus } from '@apollo/client'
import { IRisk, IRisksResponse } from 'types/graphql'
// constants
import { Modals } from 'constants/modals'
// utils
import { get } from 'lodash'
import { handleError } from 'utils/errors/handlers'

const Container = styled.div`
  height: calc(100% - 60px);
  display: flex;
  flex-direction: column;
  padding: 16px;
  border-radius: 4px;
  box-shadow:
    0 1px 3px 0 rgb(0 0 0 / 20%),
    0 1px 1px 0 rgb(0 0 0 / 14%),
    0 0 0 0 rgb(0 0 0 / 12%);

  > label {
    margin: 0 0 12px 0;
  }

  .MuiTablePagination-root {
    border: none;
  }
`

type RisksResponse = { risks: IRisksResponse }

const MultipleRelatedForm: React.FC = () => {
  const router = useRouter()

  const [searchParams, setSearchParams] = useSearchParams()

  const [riskDeleteMutation] = useMutation(RISK_DELETE_MUTATION)

  const {
    permissionsFromSecurityGroups: { scheduleRisks }
  } = useCurrentUser()

  const [page, setPage] = React.useState(Number(router.query.page) || 0)
  const [perPage, setPerPage] = React.useState(Number(router.query.perPage) || RISKS_PER_PAGE[0])

  const isScheduled = router.query.scheduled === 'true'

  const { data, networkStatus, refetch } = useQuery<RisksResponse>(RISKS_QUERY, {
    skip: !router.query.parentRiskId,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      filter: {
        recurrence: isScheduled,
        parentRiskId: router.query.parentRiskId,
        riskTemplateId: router.query.templateId
      },
      pagination: {
        pageSkip: page * perPage,
        pageTake: perPage
      }
    }
  })

  const updateUrl = (params: Record<string, string | number>) => {
    const next = new URLSearchParams(searchParams)

    Object.entries(params).forEach(([key, value]) => next.set(key, String(value)))

    setSearchParams(next, { replace: true })
  }

  const onChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
    setPage(page)
    updateUrl({ page })
  }

  const onChangePerPage = ({ target }) => {
    const perPage = target?.value || RISKS_PER_PAGE[0]

    setPage(0)
    setPerPage(parseInt(perPage))
    updateUrl({ page: 0, perPage })
  }

  const editRisk = (risk: IRisk) => (openInTab?: boolean) => {
    const pathname = `/forms/edit/${risk.id}/template/${risk.riskTemplate.id}`

    if (openInTab) {
      window.open(pathname, '_blank')
    } else {
      router.push(pathname)
    }
  }

  const showScheduled = () => {
    const next = new URLSearchParams(searchParams)

    if (isScheduled) {
      next.delete('scheduled')
    } else {
      next.set('scheduled', 'true')
    }

    setSearchParams(next)
  }

  const deleteRisk = (id: string) => async () => {
    try {
      await riskDeleteMutation({ variables: { id } })

      refetch()
    } catch (e) {
      handleError(e, 'RiskView.deleteRisk')
    }
  }

  const risks = get(data, 'risks.data', [])
  const totalCount = get(data, 'risks.pagination.totalCount', 0)
  const isLoading = networkStatus === NetworkStatus.loading || networkStatus === NetworkStatus.setVariables

  return (
    <Container>
      {scheduleRisks && (
        <FormControlLabel
          label='Schedules'
          control={<Switch checked={isScheduled} color='primary' onChange={showScheduled} />}
        />
      )}
      <RisksContainer
        risks={risks}
        isRelatedForm
        editRisk={editRisk}
        isLoading={isLoading}
        deleteRisk={deleteRisk}
        isScheduled={isScheduled}
        riskTemplateId={router.query.templateId as string}
      />
      <TablePagination
        page={page}
        count={totalCount}
        rowsPerPage={perPage}
        onPageChange={onChangePage}
        rowsPerPageOptions={RISKS_PER_PAGE}
        onRowsPerPageChange={onChangePerPage}
      />
      <ConfirmDeleteModal header='Delete Form' modalType={Modals.CONFIRM_DELETE_RISK_MODAL}>
        Are you sure you want to delete this form?
      </ConfirmDeleteModal>
    </Container>
  )
}

export default MultipleRelatedForm
